import { useState, useEffect } from 'react'
import { inventoryApi } from '../services/api'
import Input from '../components/common/Input'
import Button from '../components/common/Button'
import toast from 'react-hot-toast'

const EMPTY_FORM = {
    location: '',
    quantity: 0,
    partNumber: '',
    name: '',
    description: '',
    inventoryType: '',
    binBoxId: '',
    shelfId: '',
    series: '',
    revision: '',
    firmware: '',
    serialNumber: '',
    notes: '',
}

export default function Inventory() {
    const [loading, setLoading] = useState(true)
    const [items, setItems] = useState([])
    const [search, setSearch] = useState('')
    const [showModal, setShowModal] = useState(false)
    const [editingItem, setEditingItem] = useState(null)
    const [form, setForm] = useState(EMPTY_FORM)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        loadInventory()
    }, [])

    async function loadInventory() {
        setLoading(true)
        try {
            const response = await inventoryApi.getAll()
            if (response.success) {
                setItems(response.items || [])
            } else {
                toast.error('Failed to load inventory')
            }
        } catch (error) {
            toast.error('Error loading inventory')
        }
        setLoading(false)
    }

    async function handleSearch() {
        if (!search.trim()) {
            loadInventory()
            return
        }
        setLoading(true)
        try {
            const response = await inventoryApi.search(search)
            if (response.success) {
                setItems(response.items || [])
            }
        } catch (error) {
            toast.error('Search failed')
        }
        setLoading(false)
    }

    function openAddModal() {
        setEditingItem(null)
        setForm(EMPTY_FORM)
        setShowModal(true)
    }

    function openEditModal(item) {
        setEditingItem(item)
        setForm({
            location: item.location || '',
            quantity: item.quantity || 0,
            partNumber: item.partNumber || '',
            name: item.name || '',
            description: item.description || '',
            inventoryType: item.inventoryType || '',
            binBoxId: item.binBoxId || '',
            shelfId: item.shelfId || '',
            series: item.series || '',
            revision: item.revision || '',
            firmware: item.firmware || '',
            serialNumber: item.serialNumber || '',
            notes: item.notes || '',
        })
        setShowModal(true)
    }

    function closeModal() {
        setShowModal(false)
        setEditingItem(null)
        setForm(EMPTY_FORM)
    }

    function handleFormChange(field, value) {
        setForm(prev => ({ ...prev, [field]: value }))
    }

    async function handleSave() {
        if (!form.description.trim()) {
            toast.error('Description is required')
            return
        }

        setSaving(true)
        try {
            let response
            if (editingItem) {
                response = await inventoryApi.update(editingItem.id, form)
            } else {
                response = await inventoryApi.create(form)
            }

            if (response.success) {
                toast.success(editingItem ? 'Item updated' : 'Item created')
                closeModal()
                loadInventory()
            } else {
                toast.error(response.message || 'Failed to save')
            }
        } catch (error) {
            toast.error('Error saving item')
        }
        setSaving(false)
    }

    async function handleDelete(item) {
        if (!confirm(`Delete "${item.name || item.description}"?`)) return

        try {
            const response = await inventoryApi.delete(item.id)
            if (response.success) {
                toast.success('Item deleted')
                loadInventory()
            } else {
                toast.error('Failed to delete')
            }
        } catch (error) {
            toast.error('Error deleting item')
        }
    }

    async function handleQuantityChange(item, delta) {
        const newQty = Math.max(0, item.quantity + delta)
        try {
            const response = await inventoryApi.updateQuantity(item.id, newQty)
            if (response.success) {
                setItems(prev => prev.map(i =>
                    i.id === item.id ? { ...i, quantity: newQty } : i
                ))
            } else {
                toast.error('Failed to update quantity')
            }
        } catch (error) {
            toast.error('Error updating quantity')
        }
    }

    return (
        <div>
            {/* Header */}
            <div className="page-header flex items-center justify-between">
                <div>
                    <h1 className="page-title">Inventory</h1>
                    <p className="text-blue-100 mt-1">{items.length} items</p>
                </div>
                <Button variant="secondary" onClick={openAddModal}>
                    ➕ Add Item
                </Button>
            </div>

            {/* Search */}
            <div className="bg-white rounded-xl shadow-md p-4 mb-6">
                <div className="flex gap-2">
                    <div className="flex-1">
                        <Input
                            placeholder="Search by location, part#, name, description, shelf, bin..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        />
                    </div>
                    <Button onClick={handleSearch}>Search</Button>
                    <Button variant="secondary" onClick={() => { setSearch(''); loadInventory(); }}>
                        Clear
                    </Button>
                </div>
            </div>

            {/* Inventory List */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center h-48">
                        <div className="spinner w-8 h-8"></div>
                    </div>
                ) : items.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                        <p className="text-4xl mb-2">📦</p>
                        <p>No items found</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Qty</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Part #</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Description</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Location</th>
                                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {items.map(item => (
                                    <tr key={item.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-center gap-1">
                                                <button
                                                    onClick={() => handleQuantityChange(item, -1)}
                                                    className="w-7 h-7 bg-red-100 text-red-600 rounded hover:bg-red-200 font-bold"
                                                >
                                                    -
                                                </button>
                                                <span className="w-10 text-center font-semibold">{item.quantity}</span>
                                                <button
                                                    onClick={() => handleQuantityChange(item, 1)}
                                                    className="w-7 h-7 bg-green-100 text-green-600 rounded hover:bg-green-200 font-bold"
                                                >
                                                    +
                                                </button>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600">{item.partNumber || '-'}</td>
                                        <td className="px-4 py-3">
                                            <p className="font-medium text-gray-800">{item.name || '-'}</p>
                                            {item.inventoryType && (
                                                <span className="text-xs text-gray-500">{item.inventoryType}</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600">{item.description || '-'}</td>
                                        <td className="px-4 py-3 text-sm">
                                            <div>
                                                {item.location && (
                                                    <p className="font-bold text-gray-800">{item.location}</p>
                                                )}
                                                {item.shelfId && <span className="text-gray-600">Shelf: {item.shelfId}</span>}
                                                {item.shelfId && item.binBoxId && <br />}
                                                {item.binBoxId && <span className="text-gray-500">Bin: {item.binBoxId}</span>}
                                                {!item.location && !item.shelfId && !item.binBoxId && '-'}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-center gap-2">
                                                <button
                                                    onClick={() => openEditModal(item)}
                                                    className="text-blue-600 hover:text-blue-800"
                                                    title="Edit"
                                                >
                                                    ✏️
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(item)}
                                                    className="text-red-600 hover:text-red-800"
                                                    title="Delete"
                                                >
                                                    🗑️
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b">
                            <h2 className="text-xl font-semibold">
                                {editingItem ? 'Edit Item' : 'Add New Item'}
                            </h2>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                                    <input
                                        type="text"
                                        value={form.location}
                                        onChange={(e) => handleFormChange('location', e.target.value.toUpperCase())}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        maxLength={45}
                                        placeholder="e.g., SHOP, STORAGE UNIT"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
                                    <input
                                        type="number"
                                        value={form.quantity}
                                        onChange={(e) => handleFormChange('quantity', parseInt(e.target.value) || 0)}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        min="0"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Part Number</label>
                                    <input
                                        type="text"
                                        value={form.partNumber}
                                        onChange={(e) => handleFormChange('partNumber', e.target.value.toUpperCase())}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        maxLength={40}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                    <input
                                        type="text"
                                        value={form.name}
                                        onChange={(e) => handleFormChange('name', e.target.value.toUpperCase())}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        maxLength={40}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                                <input
                                    type="text"
                                    value={form.description}
                                    onChange={(e) => handleFormChange('description', e.target.value.toUpperCase())}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    maxLength={240}
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                                    <input
                                        type="text"
                                        value={form.inventoryType}
                                        onChange={(e) => handleFormChange('inventoryType', e.target.value.toUpperCase())}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        maxLength={40}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Serial Number</label>
                                    <input
                                        type="text"
                                        value={form.serialNumber}
                                        onChange={(e) => handleFormChange('serialNumber', e.target.value.toUpperCase())}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        maxLength={40}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Shelf</label>
                                    <input
                                        type="text"
                                        value={form.shelfId}
                                        onChange={(e) => handleFormChange('shelfId', e.target.value.toUpperCase())}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        maxLength={40}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Bin/Box</label>
                                    <input
                                        type="text"
                                        value={form.binBoxId}
                                        onChange={(e) => handleFormChange('binBoxId', e.target.value.toUpperCase())}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        maxLength={40}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Series</label>
                                    <input
                                        type="text"
                                        value={form.series}
                                        onChange={(e) => handleFormChange('series', e.target.value.toUpperCase())}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        maxLength={40}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Revision</label>
                                    <input
                                        type="text"
                                        value={form.revision}
                                        onChange={(e) => handleFormChange('revision', e.target.value.toUpperCase())}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        maxLength={40}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Firmware</label>
                                    <input
                                        type="text"
                                        value={form.firmware}
                                        onChange={(e) => handleFormChange('firmware', e.target.value.toUpperCase())}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        maxLength={40}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                <input
                                    type="text"
                                    value={form.notes}
                                    onChange={(e) => handleFormChange('notes', e.target.value.toUpperCase())}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    maxLength={100}
                                />
                            </div>
                        </div>

                        <div className="p-6 border-t flex justify-end gap-3">
                            <Button variant="secondary" onClick={closeModal}>
                                Cancel
                            </Button>
                            <Button onClick={handleSave} disabled={saving}>
                                {saving ? 'Saving...' : (editingItem ? 'Update' : 'Create')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}